import { useCallback, useEffect, useRef, useState } from "react";
import { forceReload, isUpdateAvailable } from "../../versionCheck";

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Periodically checks whether a newer version is deployed.
 * Returns { updateAvailable, applyUpdate } for UI consumption.
 * Stops polling once an update is detected.
 */
export function useVersionCheck(intervalMs: number = DEFAULT_INTERVAL_MS) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (updateAvailable) return;

    const check = async () => {
      const available = await isUpdateAvailable();
      if (available) {
        setUpdateAvailable(true);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    timerRef.current = setInterval(check, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [intervalMs, updateAvailable]);

  const applyUpdate = useCallback(() => {
    forceReload();
  }, []);

  return { updateAvailable, applyUpdate };
}
